import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { post } from '../../../Services/ApiService';
import ZohoService from '../../../Services/ZohoService';
import { ServerEndpoint } from '../../../Redux/endpoints.ts';
import OverlappingCards from '../../../Components/overlapping-course-component.js';
import ExpandableComponent from '../../Expand/expandable-component.js';
import courseData from '../../../Public/course-data.js';
import contactData from "../../../Public/contact-data.js";


const ContactForm = ({ language = 'en' }) => {
    const user = useSelector((state) => state.auth.user);
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [messageSent, setMessageSent] = useState(false);
    const [courseSelector, setCourseSelector] = useState(false);
    const [currentChoiceId, setCurrentChoiceId] = useState(null);
    const [messageText, setMessageText] = useState(language == 'en' ? 'Send' : 'שלח');
    const [selectedCourseTitle, setSelectedCourseTitle] = useState('');
    const courseFormatters = courseData.getCourses();
    const languageMap = contactData.getLanguageMap();
    const optionMappings = languageMap[language];

    useEffect(() => {
        setEmail(user?.email || '');
    }, [user]);

    const handleSelectedRecipe = async (course) => {
        const courseData = courseFormatters[course[0]];
        const courseSubject = `Course Inquiry: ${courseData.title} (${courseData.price}₪)`;
        setSubject(courseSubject);
        setSelectedCourseTitle(courseData.title);
        setCourseSelector(false); // Collapse course selector when course is selected
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Construct subject if a course is selected
        let actualSubject = subject;
        const selectedCourse = courseFormatters.find((c) => c.title === selectedCourseTitle);
        if (selectedCourse) {
            actualSubject = `Course Inquiry: ${selectedCourse.title} (${selectedCourse.price}₪)`;
        }

        const userMessage = `from : ${email} \n\nsubject : ${actualSubject}\nmessage : ${message}`;
        const subscriptionPlans = post(ServerEndpoint.CONTACT, { message: userMessage }, true, 'Subscribing...');

        subscriptionPlans.then(async (res) => {
            setMessageSent(true);
            setMessageText(res.data.status ? 'Message sent' : 'Try later');

            try {
                let accountId = null;

                if (company) {
                    const accountRes = await ZohoService.createAccount(company);
                    // accountRes may be an array or object, check both
                    accountId = accountRes?.data?.[0]?.id || accountRes?.[0]?.id || null;
                }

                const namePart = email.split('@')[0];
                const cleanName = namePart.startsWith('anon_') ? 'ANONYMOUS' : namePart.replace(/[^a-zA-Z0-9]/g, ' ').trim();

                await ZohoService.createLead({
                    email,
                    firstName: cleanName,
                    lastName: '',
                    company: company || 'COURSES_CAMPAIGN',
                    leadSource: 'COURSES_SCREEN',
                    phone: '+1234567890',
                    accountId
                });
            } catch (err) {
                console.error('Zoho lead/account creation failed:', err.message);
            }
        });
    };

    return (
        <div className='flex w-full justify-center items-center'>
            <form onSubmit={handleSubmit} className="space-y-8 w-full justify-center items-center">
                <div>
                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                        {language === 'en' ? 'Email' : 'אימייל'}
                    </label>
                    <input
                        type="email"
                        className="input-component"
                        placeholder='user@company.com'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                    {language === 'en' ? 'Company (optional)' : 'חברה (אופציונלי)'}
                  </label>
                  <input
                    type="text"
                    className="input-component"
                    placeholder='Company name'
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>

                <div>
                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                        {language === 'en' ? 'Subject' : 'נושא'}
                    </label>
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full my-4'>
                        {optionMappings.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className='button-component mx-2'
                                onClick={() => {
                                    setCourseSelector(false);
                                    setCurrentChoiceId(option.id);
                                    setSubject(option.title);
                                    option.id === 1 && setCourseSelector(true);
                                }}
                            >
                                {option.title}
                            </button>
                        ))}
                    </div>
                </div>

                {currentChoiceId === 1 && (
                  <div className='flex flex-col w-full'>
                    <h2 className="text-xl py-5 font-extrabold tracking-tight leading-none text-gray-900 dark:text-white">
                      {language === 'en' ? 'Choose a course to inquire about' : 'בחר את הקורס שתרצה לברר לגביו'}
                    </h2>
                    <ExpandableComponent collapseOnMount={!courseSelector}>
                      <div className='flex snap-start'>
                        <OverlappingCards courses={courseFormatters} trigger={handleSelectedRecipe} />
                      </div>
                    </ExpandableComponent>
                  </div>
                )}

                <div className="sm:col-span-2 mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-400">
                        {language === 'en' ? 'Free Text' : 'טקסט חופשי'}
                    </label>
                    <div className='flex flex-col my-2'>
                        {optionMappings[currentChoiceId]?.options.map((option, index) => (
                            <button
                                key={index}
                                type="button"
                                className="button-component-transparent my-2"
                                onClick={() => setMessage(option)}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                    <textarea
                        rows="6"
                        className="input-component block p-2.5 w-full"
                        placeholder={language === 'en' ? 'Type your message here' : 'כתוב כאן את ההודעה שלך'}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                    />
                </div>

                <button type="submit" className="button-component disabled:opacity-60" disabled={messageSent}>
                    {messageText}
                </button>
            </form>
        </div>
    );
};

export default ContactForm;